import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    admin: {
      sidebar: string;
      topbar: string;
      border: string;
      rowHover: string;
    };
  }
  interface PaletteOptions {
    admin?: {
      sidebar: string;
      topbar: string;
      border: string;
      rowHover: string;
    };
  }
}

export const adminTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#8A70D6',
      light: '#A593E0',
      dark: '#6A50B6',
      contrastText: '#fff',
    },
    secondary: {
      main: '#f39c12',
      light: '#f5b942',
      dark: '#c07d0e',
    },
    error: { main: '#EF4444' },
    warning: { main: '#F59E0B' },
    success: { main: '#10B981' },
    info: { main: '#3B82F6' },
    background: {
      default: '#0F0F14',
      paper: '#1A1B23',
    },
    text: {
      primary: '#F1F5F9',
      secondary: '#94A3B8',
      disabled: '#475569',
    },
    divider: 'rgba(255,255,255,0.07)',
    admin: {
      sidebar: '#13141C',
      topbar: '#1A1B23',
      border: 'rgba(255,255,255,0.07)',
      rowHover: 'rgba(138,112,214,0.07)',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Inter", sans-serif',
    fontSize: 13,
    h1: { fontWeight: 700, fontSize: '2rem', lineHeight: 1.2 },
    h2: { fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.25 },
    h3: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.3 },
    h4: { fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.35 },
    h5: { fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.4 },
    h6: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.4 },
    body1: { fontSize: '0.8125rem', lineHeight: 1.6 },
    body2: { fontSize: '0.75rem', lineHeight: 1.5 },
    caption: { fontSize: '0.6875rem', lineHeight: 1.5, letterSpacing: '0.03em' },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' },
    overline: { textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6875rem', fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: '#334155 transparent',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#334155', borderRadius: 3 },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 10,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          padding: '6px 16px',
          fontWeight: 600,
          fontSize: '0.8125rem',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(138,112,214,0.35)' },
          transition: 'transform 150ms ease, box-shadow 150ms ease',
        },
        outlined: {
          borderColor: 'rgba(255,255,255,0.12)',
          '&:hover': { borderColor: '#8A70D6', backgroundColor: 'rgba(138,112,214,0.08)' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 7,
            fontSize: '0.8125rem',
            backgroundColor: 'rgba(255,255,255,0.03)',
            '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
            '&:hover fieldset': { borderColor: 'rgba(138,112,214,0.5)' },
            '&.Mui-focused fieldset': { borderColor: '#8A70D6', borderWidth: 1.5 },
          },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          '& .MuiInputBase-input': { padding: '8px 12px' },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          fontSize: '0.8125rem',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 5,
          fontWeight: 600,
          fontSize: '0.6875rem',
          height: 22,
          letterSpacing: '0.02em',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: '#1E2030',
          border: '1px solid rgba(255,255,255,0.08)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: '#1A1B23',
          borderLeft: '1px solid rgba(255,255,255,0.07)',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '8px !important',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 44,
          '&.Mui-expanded': { minHeight: 44 },
        },
        content: { margin: '10px 0', '&.Mui-expanded': { margin: '10px 0' } },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          minHeight: 40,
          '&.Mui-selected': { color: '#8A70D6' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#8A70D6', height: 2, borderRadius: 1 },
        root: { minHeight: 40 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.06)' },
        bar: { borderRadius: 4 },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { width: 38, height: 22, padding: 0 },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            color: '#fff',
            transform: 'translateX(16px)',
            '& + .MuiSwitch-track': { opacity: 1, backgroundColor: '#8A70D6' },
          },
        },
        thumb: { width: 18, height: 18, boxShadow: 'none' },
        track: { borderRadius: 11, opacity: 1, backgroundColor: 'rgba(255,255,255,0.15)' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0F0F14',
          border: '1px solid rgba(255,255,255,0.1)',
          fontSize: '0.75rem',
          borderRadius: 6,
          fontWeight: 500,
        },
        arrow: { color: '#0F0F14' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          margin: '1px 8px',
          padding: '8px 12px',
          transition: 'background 150ms ease',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
          '&.Mui-selected': {
            backgroundColor: 'rgba(138,112,214,0.15)',
            borderLeft: '3px solid #8A70D6',
            '&:hover': { backgroundColor: 'rgba(138,112,214,0.2)' },
            '& .MuiListItemIcon-root': { color: '#8A70D6' },
            '& .MuiListItemText-primary': { color: '#A593E0', fontWeight: 700 },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: { minWidth: 34, color: '#64748B' },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'rgba(255,255,255,0.07)' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, fontSize: '0.8125rem' },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { backgroundColor: '#1E2030', border: '1px solid rgba(255,255,255,0.1)' },
        option: {
          fontSize: '0.8125rem',
          '&:hover': { backgroundColor: 'rgba(138,112,214,0.1)' },
          '&[aria-selected="true"]': { backgroundColor: 'rgba(138,112,214,0.2)' },
        },
      },
    },
  },
});
